import React, { useMemo } from 'react';
import { AlertOctagon, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MeetingInsights, SpeechSegment } from '../../../services/api.service';
import { buildAdvancedMetrics } from '../utils/advanced-metrics';
import { BenchmarkTone, buildBenchmarkPhrases } from '../utils/benchmark-insights';
import styles from './benchmark-insights.module.scss';

/** «Заголовочные» бенчмарк-фразы: цифры встречи в сравнении с нормой. */

type SpeakerTimeline = {
  name: string;
  color: string;
};

interface Props {
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  durationSec: number;
  insights: MeetingInsights | null;
}

const TONE_ICONS: Record<BenchmarkTone, React.ComponentType<{ size?: number | string }>> = {
  ok: CheckCircle2,
  warning: AlertTriangle,
  alert: AlertOctagon,
};

const TONE_CLASSES: Record<BenchmarkTone, string> = {
  ok: styles.phraseOk,
  warning: styles.phraseWarning,
  alert: styles.phraseAlert,
};

const BenchmarkInsights = ({
  segments,
  speakerTimelines,
  durationSec,
  insights,
}: Props) => {
  const { t } = useTranslation();

  const speakerColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    speakerTimelines.forEach(s => { map[s.name] = s.color; });
    return map;
  }, [speakerTimelines]);

  const metrics = useMemo(
    () => buildAdvancedMetrics(segments, durationSec, speakerColorMap),
    [segments, durationSec, speakerColorMap],
  );

  const phrases = useMemo(
    () => buildBenchmarkPhrases({
      segments,
      speakerTimelines,
      durationSec,
      metrics,
      insights,
    }),
    [segments, speakerTimelines, durationSec, metrics, insights],
  );

  if (!phrases.length) return null;

  return (
    <section className={styles.block} aria-label={t('benchmark.title')}>
      <span className={styles.label}>{t('benchmark.title')}</span>
      <ul className={styles.list}>
        {phrases.map(phrase => {
          const Icon = TONE_ICONS[phrase.tone];
          return (
            <li key={phrase.id} className={`${styles.phrase} ${TONE_CLASSES[phrase.tone]}`}>
              <span className={styles.icon} aria-hidden>
                <Icon size={16} />
              </span>
              <span className={styles.text}>
                {t(`benchmark.${phrase.textKey}`, phrase.params)}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default BenchmarkInsights;
